import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Mail } from "lucide-react";

export const EvaluationPricingCard: React.FC = () => {
  const [evaluations, setEvaluations] = useState([250]);

  const calculatePrice = (count: number) => {
    if (count <= 100) return 0.50;
    if (count <= 250) return 0.48 - ((count - 100) / 150) * 0.04;
    if (count <= 500) return 0.44 - ((count - 250) / 250) * 0.06;
    if (count <= 1000) return 0.38 - ((count - 500) / 500) * 0.08;
    return 0.30;
  };

  const currentCount = evaluations[0];
  const pricePerEvaluation = calculatePrice(currentCount);
  const totalPrice = currentCount * pricePerEvaluation;

  const features = [
    "Advanced AI evaluation",
    "Real-time scoring",
    "Detailed candidate insights",
    "Automated proctoring",
    "Priority support",
    "Custom integrations",
    "Bulk processing",
    "Expert question bank",
    "Multi-language support",
    "Analytics dashboard"
  ];

  const handleContactSales = () => {
    const subject = encodeURIComponent(`Evaluation Package - ${currentCount} evaluations`);
    const body = encodeURIComponent(`Hi,

I'm interested in purchasing ${currentCount} evaluations at $${pricePerEvaluation.toFixed(2)} per evaluation (Total: $${totalPrice.toFixed(2)}).

Please provide more information about:
- Payment options
- Implementation timeline
- Custom requirements

Best regards`);
    
    window.location.href = `mailto:sales@testera.ai?subject=${subject}&body=${body}`;
  };

  return (
    <Card className="max-w-4xl mx-auto p-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-secondary/5 shadow-2xl">

      <CardContent className="space-y-8">
        {/* Pricing Display */}
        <div className="text-center space-y-4 p-6 bg-background/50 rounded-xl border border-border/50">
          <div className="space-y-2">
            <div className="text-6xl font-bold text-foreground">
              {currentCount}
            </div>
            <div className="text-lg text-muted-foreground">evaluations</div>
          </div>
          
          <div className="flex items-center justify-center space-x-6 text-2xl">
            <div className="text-center">
              <div className="font-bold text-primary">${pricePerEvaluation.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">per evaluation</div>
            </div>
            <div className="text-muted-foreground">×</div>
            <div className="text-center">
              <div className="font-bold text-foreground">${totalPrice.toFixed(2)}</div>
              <div className="text-sm text-muted-foreground">total cost</div>
            </div>
          </div>

          {currentCount >= 500 && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
              {Math.round(((0.50 - pricePerEvaluation) / 0.50) * 100)}% savings vs. base price
            </Badge>
          )}
        </div>

        {/* Slider */}
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>50 evaluations</span>
              <span>1000 evaluations</span>
            </div>
            
            <Slider
              value={evaluations}
              onValueChange={setEvaluations}
              max={1000}
              min={50}
              step={25}
              className="w-full"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$0.50 each</span>
              <span>$0.30 each</span>
            </div>
          </div>

          {/* Price Tiers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { count: 50, price: 0.50 },
              { count: 250, price: 0.44 },
              { count: 500, price: 0.38 },
              { count: 1000, price: 0.30 }
            ].map((tier) => (
              <button
                key={tier.count}
                onClick={() => setEvaluations([tier.count])}
                className={`p-3 rounded-lg border text-center transition-all duration-200 ${
                  currentCount === tier.count
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                }`}
              >
                <div className="font-bold">{tier.count}</div>
                <div className="text-xs">${tier.price}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-bold text-lg text-foreground">What's Included:</h4>
            <div className="space-y-3">
              {features.slice(0, 5).map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-bold text-lg text-foreground">Advanced Features:</h4>
            <div className="space-y-3">
              {features.slice(5).map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="pt-6 border-t border-border/50">
          <Button
            onClick={handleContactSales}
            size="lg"
            className="w-full bg-primary hover:bg-primary-hover text-primary-foreground text-lg font-semibold py-6 shadow-lg transform hover:scale-105 transition-all duration-200"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contact Sales - Get {currentCount} Evaluations for ${totalPrice.toFixed(2)}
          </Button>
          
          <p className="text-center text-sm text-muted-foreground mt-4">
            Custom packages available for 1000+ evaluations. Volume discounts apply automatically.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default EvaluationPricingCard;